import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/client";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import { UNITE_STATUTS, PROJET_STATUTS, UNITE_CATEGORIES, fmtXOF } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { Plus, ArrowLeft, LayoutGrid, Globe, HardHat, ImagePlus, Pencil } from "lucide-react";
import { toast } from "sonner";
import PublicationPanel from "../components/PublicationPanel";
import EtagesConfigPanel from "../components/EtagesConfigPanel";
import UniteWizard from "../components/UniteWizard";
import ChantierGallery from "../components/ChantierGallery";
import UnitMediaDialog from "../components/UnitMediaDialog";
import UniteEditDialog from "../components/UniteEditDialog";
import StockPhysiquePanel from "../components/StockPhysiquePanel";

const TYPES = ["appartement", "maison", "studio", "duplex"];

export default function ImmeubleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("unites");

  const canEdit = ["admin", "secretaire", "commercial"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const canPublish = ["admin", "secretaire"].includes(user?.role);
  const [mediaUnite, setMediaUnite] = useState(null);
  const [editingUnite, setEditingUnite] = useState(null);
  // ---- Filtres du tableau des unités ----
  const [fLot, setFLot] = useState("");
  const [fCateg, setFCateg] = useState("");
  const [fStatut, setFStatut] = useState("");
  const [fTypo, setFTypo] = useState("");

  const load = async () => {
    const { data } = await api.get(`/immeubles/${id}`);
    setData(data);
  };

  // Changement de statut hors workflow = admin uniquement, motif obligatoire (audit)
  const changeStatut = async (uniteId, newStatut) => {
    const motif = window.prompt("Motif obligatoire (min. 5 caractères) :");
    if (!motif) return;
    try {
      await api.post(`/unites/${uniteId}/force-statut`, { statut: newStatut, motif });
      toast.success(`Statut mis à jour : ${UNITE_STATUTS[newStatut]?.label || newStatut}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (!data) return <Layout><div className="p-8 text-[#4B5775]">Chargement…</div></Layout>;
  const isMalika = /malika/i.test(data.nom || "");
  const canAddUnite = isMalika ? isAdmin : canEdit;

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-[1500px] mx-auto">
        <Link to="/immeubles" className="inline-flex items-center gap-2 text-xs text-[#4B5775] hover:text-[#0F1A3D] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour aux biens
        </Link>
        <PageHeader
          kicker={data.type === "maison" ? "Lot de maisons individuelles" : "Immeuble"}
          title={data.nom}
          subtitle={<span className="inline-flex items-center gap-3 flex-wrap">
            {data.statut_projet && <StatusPill statut={data.statut_projet} map={PROJET_STATUTS} />}
            <span className="text-[#4B5775]">{data.adresse}{data.quartier ? ` · ${data.quartier}` : ""} · {data.ville}</span>
          </span>}
          action={canAddUnite && (
            <button
              type="button"
              data-testid="add-unite-btn"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Nouvelle unité
            </button>
          )}
        />

        <UniteWizard open={open} onClose={() => setOpen(false)} immeubleId={id} onCreated={load} />

        <div className="flex gap-1 mb-6 border-b border-[#E2E5F1]">
          <button
            type="button"
            onClick={() => setTab("unites")}
            data-testid="tab-unites"
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition ${tab === "unites" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#4B5775] hover:text-[#0F1A3D]"}`}
          >
            <LayoutGrid className="w-4 h-4" /> Unités & Plan
          </button>
          {canPublish && (
            <button
              type="button"
              onClick={() => setTab("publication")}
              data-testid="tab-publication"
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition ${tab === "publication" ? "border-[#E91E63] text-[#E91E63]" : "border-transparent text-[#4B5775] hover:text-[#0F1A3D]"}`}
            >
              <Globe className="w-4 h-4" /> Site web {data.is_published && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2D5A3C]" />}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setTab("chantier")}
              data-testid="tab-chantier"
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition ${tab === "chantier" ? "border-[#D48A37] text-[#D48A37]" : "border-transparent text-[#4B5775] hover:text-[#0F1A3D]"}`}
            >
              <HardHat className="w-4 h-4" /> Chantier
            </button>
          )}
        </div>

        {tab === "publication" && canPublish ? (
          <div className="tpco-card p-6"><PublicationPanel immeuble={data} onUpdate={load} /></div>
        ) : (<></>)}

        {tab === "chantier" && canEdit && <ChantierGallery immeubleId={id} />}

        {tab === "unites" && (
        <>
        <StockPhysiquePanel immeubleId={id} isAdmin={isAdmin} refreshKey={data} onChanged={load} />
        {canEdit && !isMalika && (
          <div className="mb-6">
            <EtagesConfigPanel immeuble={data} onUpdate={load} />
          </div>
        )}
        <div className="tpco-card overflow-hidden mb-6" data-testid="floor-plan">
          <div className="px-6 py-4 border-b border-[#E2E5F1]">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="label-tiny mb-0.5">Plan d'occupation</div>
                <h3 className="font-display text-lg font-bold">Vue par étage</h3>
              </div>
              {/* Légende statuts */}
              <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider font-bold">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#CBE8D1] border border-[#4F9A61]"></span>
                  Disponible
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#FDE4B9] border border-[#D48A37]"></span>
                  Réservé
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#D4D0CB] border border-[#6B6B6B]"></span>
                  Vendu
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#C8D9EF] border border-[#2E64A5]"></span>
                  Loué
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#F7C9C8] border border-[#C83B3B]"></span>
                  Travaux
                </span>
              </div>
            </div>
          </div>
          {(!data.unites || data.unites.length === 0) ? (
            <div className="p-8 text-center text-sm text-[#9CA3B5]">Ajoutez des unités pour visualiser le plan.</div>
          ) : (
            <div className="p-6 space-y-5">
              {Object.entries(
                data.unites.reduce((acc, u) => {
                  const e = u.etage ?? 0;
                  acc[e] = acc[e] || [];
                  acc[e].push(u);
                  return acc;
                }, {})
              )
                .sort((a, b) => Number(b[0]) - Number(a[0]))
                .map(([etage, list]) => (
                  <div key={etage} className="flex items-stretch gap-4">
                    <div className="w-24 shrink-0 flex flex-col justify-center border-r border-[#E2E5F1] pr-4">
                      <div className="label-tiny">Étage</div>
                      <div className="font-display text-2xl font-bold text-[#0F1A3D]">{etage === "0" ? "RDC" : `+${etage}`}</div>
                    </div>
                    <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {list.map((u) => {
                        const statutMeta = {
                          disponible: { cls: "bg-[#CBE8D1] border-[#4F9A61] text-[#1F5B2F]", label: "Disponible", badge: "bg-[#4F9A61] text-white" },
                          reserve:    { cls: "bg-[#FDE4B9] border-[#D48A37] text-[#8B4E12]", label: "Réservé", badge: "bg-[#D48A37] text-white" },
                          vendu:      { cls: "bg-[#D4D0CB] border-[#6B6B6B] text-[#2B2B2B]", label: "Vendu", badge: "bg-[#6B6B6B] text-white" },
                          loue:       { cls: "bg-[#C8D9EF] border-[#2E64A5] text-[#1E4273]", label: "Loué", badge: "bg-[#2E64A5] text-white" },
                          travaux:    { cls: "bg-[#F7C9C8] border-[#C83B3B] text-[#7A1F1F]", label: "Travaux", badge: "bg-[#C83B3B] text-white" },
                        };
                        const meta = statutMeta[u.statut] || statutMeta.disponible;
                        return (
                          <div key={u.id} className={`rounded-md border-2 p-3 text-sm ${meta.cls}`} data-testid={`floor-unite-${u.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="font-bold leading-tight">{u.nom}</div>
                              <span className={`${meta.badge} text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded whitespace-nowrap`}>
                                {meta.label}
                              </span>
                            </div>
                            <div className="text-[10px] uppercase tracking-wider opacity-80">{u.type_unite}</div>
                            <div className="mt-1.5 text-[11px] font-mono">
                              {u.surface_m2 ? `${u.surface_m2} m² · ` : ""}{u.nb_chambres ? `${u.nb_chambres} ch` : ""}
                            </div>
                            {u.categorie && u.categorie !== "standard" && (
                              <div className="mt-1 text-[9px] uppercase tracking-wider opacity-70 font-bold">
                                {UNITE_CATEGORIES[u.categorie]?.label}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="tpco-card overflow-hidden" data-testid="unites-table">
          <div className="px-6 py-4 border-b border-[#E2E5F1]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold">Unités · {data.unites?.length || 0}</h3>
              {(fLot || fCateg || fStatut || fTypo) && (
                <button
                  type="button"
                  onClick={() => { setFLot(""); setFCateg(""); setFStatut(""); setFTypo(""); }}
                  className="text-[10px] uppercase tracking-wider text-[#4B5775] hover:text-[#E91E63] font-bold"
                  data-testid="btn-clear-filters"
                >
                  ✕ Effacer filtres
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <input
                type="text"
                value={fLot}
                onChange={(e) => setFLot(e.target.value)}
                placeholder="Rechercher (N° lot, nom…)"
                className="h-9 px-3 rounded-md border border-[#E2E5F1] text-xs focus:outline-none focus:border-[#1E3A8A]"
                data-testid="filter-unite-search"
              />
              <select
                value={fTypo}
                onChange={(e) => setFTypo(e.target.value)}
                className="h-9 px-2 rounded-md border border-[#E2E5F1] text-xs bg-white focus:outline-none focus:border-[#1E3A8A]"
                data-testid="filter-unite-typo"
              >
                <option value="">Toutes typologies</option>
                {[...new Set((data.unites || []).map((u) => u.typologie_code || "UNKNOWN"))].sort().map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              <select
                value={fCateg}
                onChange={(e) => setFCateg(e.target.value)}
                className="h-9 px-2 rounded-md border border-[#E2E5F1] text-xs bg-white focus:outline-none focus:border-[#1E3A8A]"
                data-testid="filter-unite-categ"
              >
                <option value="">Toutes catégories</option>
                {Object.entries(UNITE_CATEGORIES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <select
                value={fStatut}
                onChange={(e) => setFStatut(e.target.value)}
                className="h-9 px-2 rounded-md border border-[#E2E5F1] text-xs bg-white focus:outline-none focus:border-[#1E3A8A]"
                data-testid="filter-unite-statut"
              >
                <option value="">Tous statuts</option>
                {Object.entries(UNITE_STATUTS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
          {(!data.unites || data.unites.length === 0) ? (
            <div className="p-12 text-center text-sm text-[#9CA3B5]">Aucune unité. Créez-en une.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3FB] text-[#4B5775] text-xs uppercase tracking-[0.05em]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">N° Lot</th>
                  <th className="text-left px-6 py-3 font-semibold">Unité</th>
                  <th className="text-left px-4 py-3 font-semibold">Catégorie</th>
                  <th className="text-left px-4 py-3 font-semibold">Type</th>
                  <th className="text-left px-4 py-3 font-semibold">Étage</th>
                  <th className="text-left px-4 py-3 font-semibold">Surface</th>
                  <th className="text-right px-4 py-3 font-semibold">Prix réf.</th>
                  <th className="text-right px-4 py-3 font-semibold">Loyer réf.</th>
                  <th className="text-left px-4 py-3 font-semibold">Statut</th>
                  {canEdit && <th className="text-right px-4 py-3 font-semibold">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E5F1]">
                {data.unites
                  .filter((u) => {
                    if (fLot) {
                      const q = fLot.toLowerCase().trim();
                      const hay = `${u.numero_lot || ""} ${u.nom || ""}`.toLowerCase();
                      if (!hay.includes(q)) return false;
                    }
                    if (fTypo && (u.typologie_code || "UNKNOWN") !== fTypo) return false;
                    if (fCateg && u.categorie !== fCateg) return false;
                    if (fStatut && u.statut !== fStatut) return false;
                    return true;
                  })
                  .map((u) => (
                  <tr key={u.id} className="hover:bg-[#F8FAFC] transition-colors" data-testid={`unite-row-${u.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-[#4B5775]">{u.numero_lot || <span className="text-[#9CA3B5]">—</span>}</td>
                    <td className="px-6 py-3 font-semibold text-[#0F1A3D]">{u.nom}</td>
                    <td className="px-4 py-3">{u.categorie ? <StatusPill statut={u.categorie} map={UNITE_CATEGORIES} /> : <span className="text-xs text-[#9CA3B5]">—</span>}</td>
                    <td className="px-4 py-3 capitalize text-[#4B5775]">{u.type_unite}</td>
                    <td className="px-4 py-3 text-[#4B5775]">{u.etage}</td>
                    <td className="px-4 py-3 text-[#4B5775]">{u.surface_m2 ? `${u.surface_m2} m²` : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-[#0F1A3D]">{u.prix_vente ? fmtXOF(u.prix_vente) : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-[#0F1A3D]">{u.prix_location ? fmtXOF(u.prix_location) : "—"}</td>
                    <td className="px-4 py-3"><StatusPill statut={u.statut} map={UNITE_STATUTS} /></td>
                    {canEdit && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setEditingUnite(u)}
                          className="inline-flex items-center gap-1 h-7 px-2.5 mr-1 rounded-md border border-[#E2E5F1] hover:border-[#1E3A8A] hover:bg-[#E8EBFA] text-[#0F1A3D] text-[10px] font-bold uppercase tracking-wider transition"
                          data-testid={`btn-edit-unite-${u.id}`}
                        >
                          <Pencil className="w-3 h-3" /> Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => setMediaUnite(u)}
                          className="inline-flex items-center gap-1 h-7 px-2.5 mr-1 rounded-md bg-[#1E3A8A]/10 hover:bg-[#1E3A8A] hover:text-white text-[#1E3A8A] text-[10px] font-bold uppercase tracking-wider transition"
                          data-testid={`btn-media-${u.id}`}
                          title="Photos, 3D et vidéos"
                        >
                          <ImagePlus className="w-3 h-3" /> Médias
                        </button>
                        {isAdmin && <select
                          value={u.statut}
                          onChange={async (e) => {
                            const newStatut = e.target.value;
                            if (newStatut === u.statut) return;
                            const label = UNITE_STATUTS[newStatut]?.label || newStatut;
                            if (window.confirm(`Changer le statut de "${u.nom}" en ${label.toUpperCase()} ?`)) {
                              await changeStatut(u.id, newStatut);
                            }
                          }}
                          className="h-7 px-2 rounded-md border border-[#E2E5F1] bg-white text-[#0F1A3D] text-[11px] font-semibold focus:outline-none focus:border-[#1E3A8A] cursor-pointer hover:border-[#1E3A8A] transition"
                          data-testid={`statut-select-${u.id}`}
                          title="Forcer le statut (admin, motif obligatoire)"
                        >
                          {Object.entries(UNITE_STATUTS).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                          ))}
                        </select>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
        )}
      </div>
      <UnitMediaDialog open={!!mediaUnite} onClose={() => setMediaUnite(null)} unite={mediaUnite} />
      <UniteEditDialog open={!!editingUnite} onClose={() => setEditingUnite(null)} unite={editingUnite} onUpdated={load} />
    </Layout>
  );
}
